import type { ReactNode } from "react";
import { SlideStatement } from "../../../SlideStatement";
import { SupLink } from "../../../SlideContext";

export default function WhyNow() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:px-24 md:pt-16 md:pb-8 md:[&_header]:mb-0">
      <SlideStatement eyebrow="Why Now">
        <div>
          <h1 className="text-3xl font-semibold leading-[1.05] tracking-tight md:text-7xl">
            Four trends, converging now.
          </h1>
          <p className="mt-4 text-xl font-medium tracking-tight text-slate-600 md:mt-5 md:text-2xl">
            Demand exploded. The cost to build collapsed. We have the
            expertise for a wedge.
          </p>
        </div>
      </SlideStatement>

      {/* Below the rule — the four trends. */}
      <div className="flex flex-col gap-4 md:min-h-0 md:flex-1 md:justify-center md:gap-4 md:pt-6">

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-4">
        <Force
          number="1"
          name="App Supply Explosion"
          lede="AI coding is flooding the stores."
        >
          AI coding collapsed the cost of shipping an app: Q1 2026 saw{" "}
          <span className="font-semibold text-slate-900">
            +84% YoY new App Store submissions
          </span>
          <SupLink to="sources">2</SupLink>. Every existing app now competes
          against a flood of fresh listings.
        </Force>
        <Force
          number="2"
          name="Discovery Shift"
          lede="Discovery splintered, ranking went semantic."
        >
          Apple's ranking is LLM-fine-tuned<SupLink to="sources">3</SupLink>,
          Google shipped Ask Play<SupLink to="sources">7</SupLink>, and the AI
          assistants now shape where users look<SupLink to="sources">4</SupLink>.{" "}
          <span className="font-semibold text-slate-900">
            Two-store, search-only, keyword-stuffed ASO is over.
          </span>
        </Force>
        <Force
          number="3"
          name="Localization Mandate"
          lede="Growth now requires going global."
        >
          Global IAP hit $167B in 2025<SupLink to="sources">6</SupLink>, most
          of it outside any single market — and AI translation just made{" "}
          <span className="font-semibold text-slate-900">
            localization across 180+ markets
          </span>{" "}
          affordable for mid-market publishers.
        </Force>
        <Force
          number="4"
          name="The Build Arbitrage"
          lede="Building it finally costs a fraction of what it did."
          highlighted
        >
          What cost <span className="font-semibold text-slate-900">$22–25M
          and 26 FTE over 24 months</span> in 2022 now costs a fraction.{" "}
          <span className="font-semibold text-slate-900">
            A market that couldn't justify a venture-scale platform a few
            years ago absolutely can today
          </span>{" "}
          — and legacy codebases keep incumbents from seizing it.
        </Force>
      </div>

      {/* The flip side of force 1: the tooling market flooded too, which is
          what turns proof into the entry ticket. Deliberately stops at the
          market condition — the payoff is slide 12's to make, and claiming it
          here, before any evidence, is the assertion this card warns about
          (TPF, 2026-09-08). */}
      </div>

    </div>
  );
}

function Force({
  number,
  name,
  lede,
  children,
  highlighted,
}: {
  number: string;
  name: string;
  lede: string;
  children: ReactNode;
  highlighted?: boolean;
}) {
  return (
    <div
      className={`flex flex-col rounded-xl p-5 md:p-5 ${
        highlighted
          ? "bg-blue-50 ring-2 ring-blue-400"
          : "bg-white shadow-sm ring-1 ring-slate-200"
      }`}
    >
      <div className="flex items-center gap-3 md:gap-4">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white md:h-9 md:w-9 md:text-base">
          {number}
        </span>
        <h3 className="text-xl font-bold leading-tight tracking-tight text-blue-600 md:text-2xl">
          {name}
        </h3>
      </div>
      <p className="mt-3 text-base font-semibold leading-snug text-slate-900 md:mt-2 md:text-lg">
        {lede}
      </p>
      <p className="mt-3 text-sm leading-relaxed text-slate-600 md:mt-2 md:text-base md:leading-snug">
        {children}
      </p>
    </div>
  );
}
